import React from "react";
import PropTypes from "prop-types";

/**
 * SectionCard — the shared profile section-card primitive.
 *
 * A white Bootstrap card (the stacked-section-card chrome from Alyssa's V2 designs):
 * an optional Bold-18 title on the left, an optional edit pen / custom controls on the
 * right, and arbitrary section content in the body. Model-agnostic: it knows nothing
 * about Id models — the engine passes a title, an `onEdit` callback, and children.
 *
 * Edit affordances are intentionally per-section and explicit (Company's are scattered):
 * pass `editable` + `onEdit` to get the standard pen, or pass `headerActions` to render
 * a section's own control(s). The card is `position: relative` so a section that renders
 * its own absolutely-positioned control still anchors to the card.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.title] - Section title (Bold-18 when a string)
 * @param {boolean} [props.editable] - Whether the viewer may edit this section
 * @param {Function} [props.onEdit] - Click handler for the standard edit pen (shown only when editable)
 * @param {string} [props.editButtonId] - Optional DOM id for the edit pen (stable hook for system tests)
 * @param {React.ReactNode} [props.headerActions] - Custom header-right controls (rendered before the pen)
 * @param {string} [props.id] - Optional DOM id (e.g. for system tests / deep links)
 * @param {string} [props.className] - Extra classes appended to the card
 * @param {React.ReactNode} props.children - Section body
 */
function SectionCard({
  title = null,
  editable = false,
  onEdit = null,
  editButtonId = undefined,
  headerActions = null,
  id = undefined,
  className = "",
  children,
}) {
  const showPen = editable && typeof onEdit === "function";
  const showHeader = title != null || headerActions != null || showPen;

  return (
    <div
      id={id}
      className={`card profile-section-card ${className}`.trim()}
      style={{
        position: "relative",
        // Match Alyssa's section-card recipe: 8px radius + Card/Shadow (bare Bootstrap .card is 6px, flat)
        borderRadius: "8px",
        boxShadow: "0px 1px 2px -1px rgba(10, 13, 18, 0.1), 0px 1px 3px 0px rgba(10, 13, 18, 0.1)",
      }}
    >
      {showHeader && (
        // The header's separation from the body belongs to the header, but only when a body
        // follows — a card rendering header-only (e.g. a collapsed checklist) stays vertically
        // centered instead of carrying a phantom bottom gap.
        <div className={`d-flex flex-row justify-content-between align-items-center${children ? " mb-3" : ""}`}>
          {title != null ? (
            // Lato Bold 18/28 (the design's Text-lg/fw-bold)
            <h2 className="fw-bold m-0" style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}>
              {title}
            </h2>
          ) : (
            <span />
          )}
          <div className="d-flex align-items-center">
            {headerActions}
            {showPen && (
              <button
                id={editButtonId}
                type="button"
                className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
                style={{ width: "2rem", height: "2rem", padding: 0 }}
                onClick={onEdit}
                aria-label={typeof title === "string" ? `Edit ${title}` : "Edit section"}
              >
                <i className="fa-solid fa-pencil-alt" />
              </button>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

SectionCard.propTypes = {
  title: PropTypes.node,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  editButtonId: PropTypes.string,
  headerActions: PropTypes.node,
  id: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default SectionCard;
